import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({ email: '' });
  const [openWeek, setOpenWeek] = useState<number | null>(null);
  const [bannerVisible, setBannerVisible] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email) return;
    
    try {
      const formElement = e.target as HTMLFormElement;
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('email', formData.email);

      const response = await fetch('https://readdy.ai/api/form/d5re1e6tc768qf4bqis0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString(),
      });

      if (response.ok) {
        setFormSubmitted(true);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const toggleWeek = (weekNumber: number) => {
    setOpenWeek(openWeek === weekNumber ? null : weekNumber);
  };

  const weeks = [
    {
      number: 1,
      phase: 1,
      title: "The Identity Shift",
      description: "Moving from student to clinician. Navigating impostor syndrome vs. actual incompetence and owning your authority."
    },
    {
      number: 2,
      phase: 1,
      title: "The Clinical Logic Engine",
      description: "Building usable differentials and learning to tolerate clinical uncertainty without panic."
    },
    {
      number: 3,
      phase: 1,
      title: "High-Stakes Safety",
      description: "Mastering suicide risk assessment and defensive documentation that protects your license and your patients."
    },
    {
      number: 4,
      phase: 1,
      title: "Boundaries & Containment",
      description: "The art of saying \"no\" without guilt, managing difficult expectations, and establishing professional authority."
    },
    {
      number: 5,
      phase: 2,
      title: "Real-World Psychopharmacology",
      description: "Moving beyond the textbook. How to start safely, titrate thoughtfully, and manage \"medication-seeking\" or disengaged patients."
    },
    {
      number: 6,
      phase: 2,
      title: "The 20-Minute Therapeutic Visit",
      description: "Using CBT/ACT micro-skills and supportive therapy during standard medication appointments."
    },
    {
      number: 7,
      phase: 2,
      title: "Complex Case Reasoning",
      description: "Live case-based discussions: \"What would you do next?\" Managing difficult outcomes and the \"I'm fine\" patient."
    },
    {
      number: 8,
      phase: 2,
      title: "Your First-Year Roadmap",
      description: "Developing your unique clinical style, leveraging your natural strengths, and knowing what actually matters for long-term burnout prevention."
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Founding Member Banner */}
      {bannerVisible && (
        <div className="bg-[#8B9B8E] text-white py-3 px-4 sm:px-6 relative">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 sm:gap-3 text-center pr-8">
            <i className="ri-star-fill text-xs sm:text-sm"></i>
            <p className="text-xs sm:text-sm font-medium">
              Inaugural Cohort — Founding Member Enrollment Opening Soon
            </p>
            <i className="ri-star-fill text-xs sm:text-sm"></i>
          </div>
          <button
            onClick={() => setBannerVisible(false)}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center hover:bg-white/10 rounded-full transition-colors cursor-pointer"
            aria-label="Close banner"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
      )}

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <img 
              src="https://public.readdy.ai/ai/img_res/2000139d-511f-45b0-ba23-8d9ee9829d39.png" 
              alt="Clinical Mentorship Logo" 
              className="h-10 sm:h-12 w-auto"
            />
            <a 
              href="#waitlist" 
              className="px-4 sm:px-6 py-2 sm:py-2.5 bg-[#2B2B2B] text-white rounded-full text-xs sm:text-sm font-medium hover:bg-[#1a1a1a] transition-colors cursor-pointer whitespace-nowrap active:scale-95"
            >
              Join Waitlist
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-10 gap-8 sm:gap-12 mb-12 sm:mb-16">
            <div className="lg:col-span-7">
              <p className="text-xs uppercase tracking-wider text-[#6B6B6B] mb-4 sm:mb-6 font-medium">Clinical Mentorship</p>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif text-[#2B2B2B] mb-4 sm:mb-6 leading-tight">
                From New Graduate to Confident Clinician
              </h1>
              <p className="text-lg sm:text-xl text-[#6B6B6B] font-light leading-relaxed">
                An 8-week clinical mentorship for PMHNPs focused on safe decision-making, clinical identity, and the reality of independent practice.
              </p>
            </div>
            <div className="lg:col-span-3 flex items-center justify-center order-first lg:order-last">
              <img 
                src="https://static.readdy.ai/image/c0f393addafe305687bbcc604ae743d2/ab35313c5ba3dad7101a2a25ed407226.png"
                alt="Margaret Ngumi, FNP-C / PMHNP-BC"
                className="w-40 h-40 sm:w-48 sm:h-48 rounded-3xl object-cover object-top border-2 border-[#8B9B8E]/30"
              />
            </div>
          </div>

          <div className="bg-[#F5F5F3] rounded-2xl p-6 sm:p-8 lg:p-12">
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-serif text-[#2B2B2B] mb-3">
              The Outcome: In 8 Weeks, You Will...
            </h3>
            <p className="text-sm sm:text-base text-[#6B6B6B] mb-6 sm:mb-8 leading-relaxed">
              This section targets the internal shift from "imposter" to "professional."
            </p>
            <div className="space-y-5 sm:space-y-6">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-2 h-2 rounded-full bg-[#8B9B8E] mt-2 flex-shrink-0"></div>
                <div>
                  <p className="text-[#2B2B2B] font-semibold mb-1 text-sm sm:text-base">Own Your Clinical Authority</p>
                  <p className="text-[#6B6B6B] text-sm sm:text-base">Move past the "asking for permission" phase and start owning your treatment plans with evidence-based conviction.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-2 h-2 rounded-full bg-[#8B9B8E] mt-2 flex-shrink-0"></div>
                <div>
                  <p className="text-[#2B2B2B] font-semibold mb-1 text-sm sm:text-base">Establish Defensive Mastery</p>
                  <p className="text-[#6B6B6B] text-sm sm:text-base">Master documentation that doesn't just check boxes, but tells a clear clinical story that protects your license.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-2 h-2 rounded-full bg-[#8B9B8E] mt-2 flex-shrink-0"></div>
                <div>
                  <p className="text-[#2B2B2B] font-semibold mb-1 text-sm sm:text-base">Achieve Professional Containment</p>
                  <p className="text-[#6B6B6B] text-sm sm:text-base">Learn to lead a visit—managing "chatty" patients, difficult requests, and time-pressure without feeling like you've compromised care.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-2 h-2 rounded-full bg-[#8B9B8E] mt-2 flex-shrink-0"></div>
                <div>
                  <p className="text-[#2B2B2B] font-semibold mb-1 text-sm sm:text-base">Gain Clinical Peace of Mind</p>
                  <p className="text-[#6B6B6B] text-sm sm:text-base">Close your laptop at the end of the day without the "mental loop" of wondering if you missed a red flag.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-center items-stretch sm:items-center gap-3 sm:gap-4 mt-8 sm:mt-12">
            <a 
              href="#waitlist" 
              className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 bg-[#2B2B2B] text-white rounded-full text-sm sm:text-base font-medium hover:bg-[#1a1a1a] transition-all cursor-pointer whitespace-nowrap active:scale-95"
            >
              Join the Inaugural Cohort
              <i className="ri-arrow-right-line"></i>
            </a>
            <a 
              href="#curriculum" 
              className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 bg-white border-2 border-[#2B2B2B] text-[#2B2B2B] rounded-full text-sm sm:text-base font-medium hover:bg-[#FAFAF8] transition-all cursor-pointer whitespace-nowrap active:scale-95"
            >
              View the Roadmap
              <i className="ri-arrow-down-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Why This Mentorship Exists */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-12">
        <div className="max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-wider text-[#8B9B8E] text-center mb-6 sm:mb-8 font-medium">Why This Mentorship Exists</p>
          
          <div className="space-y-5 sm:space-y-6 text-[#2B2B2B] text-base sm:text-lg leading-relaxed">
            <p>
              New PMHNPs often step into practice carrying full clinical responsibility before they feel grounded in their decision making.
            </p>
            <p>
              School teaches foundations.
            </p>
            <p>
              Real-world psychiatry brings complexity, uncertainty, and time pressure that school can't prepare you for.
            </p>
            <p>
              This mentorship exists to help you think clearly, practice safely, and build confidence over time, without aiming for perfection.
            </p>
          </div>
        </div>
      </section>

      {/* Comparison Table Section - Mobile Optimized */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#2B2B2B] mb-4 sm:mb-6">
              School & Boards vs. This Mentorship
            </h2>
            <p className="text-base sm:text-lg text-[#6B6B6B] max-w-2xl mx-auto leading-relaxed">
              Understanding the difference between academic preparation and real-world clinical confidence
            </p>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[#FAFAF8]">
                  <th className="text-left p-6 text-xl font-semibold text-[#2B2B2B] border-b-2 border-[#8B9B8E] rounded-tl-xl">
                    School & Boards
                  </th>
                  <th className="text-left p-6 text-xl font-semibold text-[#2B2B2B] border-b-2 border-[#8B9B8E] rounded-tr-xl">
                    This Mentorship
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[#E5E5E5]">
                  <td className="p-6 text-[#2B2B2B] align-top">
                    <p className="leading-relaxed">Memorizing Dosages</p>
                  </td>
                  <td className="p-6 text-[#2B2B2B] align-top">
                    <p className="leading-relaxed">Clinical Decision-Making</p>
                  </td>
                </tr>
                <tr className="border-b border-[#E5E5E5] bg-[#FAFAF8]/50">
                  <td className="p-6 text-[#2B2B2B] align-top">
                    <p className="leading-relaxed">Idealized Scenarios</p>
                  </td>
                  <td className="p-6 text-[#2B2B2B] align-top">
                    <p className="leading-relaxed">Real-World "Messy" Cases</p>
                  </td>
                </tr>
                <tr className="border-b border-[#E5E5E5]">
                  <td className="p-6 text-[#2B2B2B] align-top">
                    <p className="leading-relaxed">Testing Knowledge</p>
                  </td>
                  <td className="p-6 text-[#2B2B2B] align-top">
                    <p className="leading-relaxed">Building Professional Confidence</p>
                  </td>
                </tr>
                <tr className="border-b border-[#E5E5E5] bg-[#FAFAF8]/50">
                  <td className="p-6 text-[#2B2B2B] align-top">
                    <p className="leading-relaxed">Working in Isolation</p>
                  </td>
                  <td className="p-6 text-[#2B2B2B] align-top">
                    <p className="leading-relaxed">Small Group Community</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4">
            <div className="bg-[#FAFAF8] rounded-xl p-5 border border-[#E5E5E5]">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-[#8B9B8E]"></div>
                <h3 className="text-sm font-semibold text-[#8B9B8E] uppercase tracking-wide">School & Boards</h3>
              </div>
              <p className="text-[#2B2B2B] leading-relaxed mb-3">Memorizing Dosages</p>
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-arrow-down-line text-[#8B9B8E]"></i>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full bg-[#2B2B2B]"></div>
                <h3 className="text-sm font-semibold text-[#2B2B2B] uppercase tracking-wide">This Mentorship</h3>
              </div>
              <p className="text-[#2B2B2B] leading-relaxed font-medium">Clinical Decision-Making</p>
            </div>

            <div className="bg-white rounded-xl p-5 border border-[#E5E5E5]">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-[#8B9B8E]"></div>
                <h3 className="text-sm font-semibold text-[#8B9B8E] uppercase tracking-wide">School & Boards</h3>
              </div>
              <p className="text-[#2B2B2B] leading-relaxed mb-3">Idealized Scenarios</p>
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-arrow-down-line text-[#8B9B8E]"></i>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full bg-[#2B2B2B]"></div>
                <h3 className="text-sm font-semibold text-[#2B2B2B] uppercase tracking-wide">This Mentorship</h3>
              </div>
              <p className="text-[#2B2B2B] leading-relaxed font-medium">Real-World "Messy" Cases</p>
            </div>

            <div className="bg-[#FAFAF8] rounded-xl p-5 border border-[#E5E5E5]">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-[#8B9B8E]"></div>
                <h3 className="text-sm font-semibold text-[#8B9B8E] uppercase tracking-wide">School & Boards</h3>
              </div>
              <p className="text-[#2B2B2B] leading-relaxed mb-3">Testing Knowledge</p>
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-arrow-down-line text-[#8B9B8E]"></i>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full bg-[#2B2B2B]"></div>
                <h3 className="text-sm font-semibold text-[#2B2B2B] uppercase tracking-wide">This Mentorship</h3>
              </div>
              <p className="text-[#2B2B2B] leading-relaxed font-medium">Building Professional Confidence</p>
            </div>

            <div className="bg-white rounded-xl p-5 border border-[#E5E5E5]">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-[#8B9B8E]"></div>
                <h3 className="text-sm font-semibold text-[#8B9B8E] uppercase tracking-wide">School & Boards</h3>
              </div>
              <p className="text-[#2B2B2B] leading-relaxed mb-3">Working in Isolation</p>
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-arrow-down-line text-[#8B9B8E]"></i>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full bg-[#2B2B2B]"></div>
                <h3 className="text-sm font-semibold text-[#2B2B2B] uppercase tracking-wide">This Mentorship</h3>
              </div>
              <p className="text-[#2B2B2B] leading-relaxed font-medium">Small Group Community</p>
            </div>
          </div>

          <div className="mt-8 sm:mt-12 bg-[#8B9B8E]/10 border border-[#8B9B8E]/20 rounded-xl p-6 sm:p-8 text-center">
            <p className="text-[#2B2B2B] text-base sm:text-lg leading-relaxed">
              <strong className="font-semibold">This mentorship doesn't replace your education.</strong> It builds on it by focusing on the clinical judgment, confidence, and real-world decision-making that only comes with guided practice.
            </p>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-8 sm:gap-12 items-start">
            <div className="lg:col-span-2">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#2B2B2B] leading-tight lg:sticky lg:top-32">
                Who This Mentorship Is For
              </h2>
            </div>
            
            <div className="lg:col-span-3 bg-[#FAFAF8] rounded-2xl p-6 sm:p-8 lg:p-12">
              <p className="text-[#2B2B2B] text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed">
                This mentorship is designed specifically for new graduate PMHNPs who are in the early stages of independent practice.
              </p>
              
              <p className="text-[#2B2B2B] font-medium mb-5 sm:mb-6">It's for you if:</p>
              
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#8B9B8E] text-xl flex-shrink-0 mt-0.5"></i>
                  <p className="text-[#2B2B2B] text-sm sm:text-base">You've recently graduated and are adjusting to full clinical responsibility</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#8B9B8E] text-xl flex-shrink-0 mt-0.5"></i>
                  <p className="text-[#2B2B2B] text-sm sm:text-base">You find yourself second-guessing decisions after sessions</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#8B9B8E] text-xl flex-shrink-0 mt-0.5"></i>
                  <p className="text-[#2B2B2B] text-sm sm:text-base">You want support thinking through complex cases — not just guidelines</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#8B9B8E] text-xl flex-shrink-0 mt-0.5"></i>
                  <p className="text-[#2B2B2B] text-sm sm:text-base">You're working in outpatient or telehealth settings</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#8B9B8E] text-xl flex-shrink-0 mt-0.5"></i>
                  <p className="text-[#2B2B2B] text-sm sm:text-base">You want to build confidence, judgment, and safety habits early in your career</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What This Is / Isn't */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
            {/* What This Is */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 lg:p-10 border border-[#E5E5E5]">
              <h3 className="text-xl sm:text-2xl font-semibold text-[#2B2B2B] mb-5 sm:mb-6">What This Is</h3>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#8B9B8E] mt-2.5 flex-shrink-0"></div>
                  <p className="text-[#2B2B2B] text-sm sm:text-base">An educational, case-based clinical mentorship</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#8B9B8E] mt-2.5 flex-shrink-0"></div>
                  <p className="text-[#2B2B2B] text-sm sm:text-base">Designed specifically for new graduate PMHNPs</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#8B9B8E] mt-2.5 flex-shrink-0"></div>
                  <p className="text-[#2B2B2B] text-sm sm:text-base">Focused on outpatient and telehealth decision-making</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#8B9B8E] mt-2.5 flex-shrink-0"></div>
                  <p className="text-[#2B2B2B] text-sm sm:text-base">Weekly discussions with no extra homework or assignments</p>
                </div>
              </div>
            </div>

            {/* What This Isn't */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 lg:p-10 border border-[#E5E5E5]">
              <h3 className="text-xl sm:text-2xl font-semibold text-[#2B2B2B] mb-5 sm:mb-6">What This Isn't</h3>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#6B6B6B] mt-2.5 flex-shrink-0"></div>
                  <p className="text-[#2B2B2B] text-sm sm:text-base">Not formal clinical or licensure supervision</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#6B6B6B] mt-2.5 flex-shrink-0"></div>
                  <p className="text-[#2B2B2B] text-sm sm:text-base">Not job placement, résumé help, or interview coaching</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#6B6B6B] mt-2.5 flex-shrink-0"></div>
                  <p className="text-[#2B2B2B] text-sm sm:text-base">Not private practice or business mentorship</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#6B6B6B] mt-2.5 flex-shrink-0"></div>
                  <p className="text-[#2B2B2B] text-sm sm:text-base">Not advanced subspecialty training for experienced clinicians</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Focus On */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-12 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#2B2B2B] text-center mb-4 sm:mb-6">
            The Reality: What We Focus On
          </h2>
          <p className="text-base sm:text-lg text-[#6B6B6B] text-center max-w-2xl mx-auto mb-3 sm:mb-4 leading-relaxed">
            This section addresses the "messy" real-world scenarios textbooks don't cover.
          </p>
          <p className="text-base sm:text-lg text-[#2B2B2B] text-center max-w-3xl mx-auto mb-8 sm:mb-12 leading-relaxed font-medium">
            We focus on the friction points of modern practice. We spend our time on the things that actually keep you up at night:
          </p>

          <div className="grid sm:grid-cols-2 gap-5 sm:gap-6">
            <div className="bg-[#FAFAF8] rounded-2xl p-6 sm:p-8 border border-[#E5E5E5]">
              <div className="w-12 h-12 flex items-center justify-center bg-[#8B9B8E]/10 rounded-xl mb-4">
                <i className="ri-user-search-line text-2xl text-[#8B9B8E]"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-[#2B2B2B] mb-2 sm:mb-3">The "I'm Fine" Patient</h3>
              <p className="text-[#6B6B6B] leading-relaxed text-sm sm:text-base">
                How to build a differential when the patient gives you nothing to work with.
              </p>
            </div>

            <div className="bg-[#FAFAF8] rounded-2xl p-6 sm:p-8 border border-[#E5E5E5]">
              <div className="w-12 h-12 flex items-center justify-center bg-[#8B9B8E]/10 rounded-xl mb-4">
                <i className="ri-capsule-line text-2xl text-[#8B9B8E]"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-[#2B2B2B] mb-2 sm:mb-3">The Controlled Substance Pivot</h3>
              <p className="text-[#6B6B6B] leading-relaxed text-sm sm:text-base">
                Navigating requests for stimulants or benzos while maintaining a strong therapeutic alliance.
              </p>
            </div>

            <div className="bg-[#FAFAF8] rounded-2xl p-6 sm:p-8 border border-[#E5E5E5]">
              <div className="w-12 h-12 flex items-center justify-center bg-[#8B9B8E]/10 rounded-xl mb-4">
                <i className="ri-brain-line text-2xl text-[#8B9B8E]"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-[#2B2B2B] mb-2 sm:mb-3">The ADHD "Social Media" Wave</h3>
              <p className="text-[#6B6B6B] leading-relaxed text-sm sm:text-base">
                How to evaluate ADHD vs. "not ADHD" with structure, clarity, and confidence.
              </p>
            </div>

            <div className="bg-[#FAFAF8] rounded-2xl p-6 sm:p-8 border border-[#E5E5E5]">
              <div className="w-12 h-12 flex items-center justify-center bg-[#8B9B8E]/10 rounded-xl mb-4">
                <i className="ri-video-line text-2xl text-[#8B9B8E]"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-[#2B2B2B] mb-2 sm:mb-3">Telehealth Safety Cues</h3>
              <p className="text-[#6B6B6B] leading-relaxed text-sm sm:text-base">
                Spotting subtle risk factors through a screen and knowing exactly when (and how) to escalate.
              </p>
            </div>

            <div className="bg-[#FAFAF8] rounded-2xl p-6 sm:p-8 border border-[#E5E5E5]">
              <div className="w-12 h-12 flex items-center justify-center bg-[#8B9B8E]/10 rounded-xl mb-4">
                <i className="ri-shield-check-line text-2xl text-[#8B9B8E]"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-[#2B2B2B] mb-2 sm:mb-3">The Defensive Documentation Shield</h3>
              <p className="text-[#6B6B6B] leading-relaxed text-sm sm:text-base">
                Charting for high-stress encounters in a way that protects your license without taking hours.
              </p>
            </div>

            <div className="bg-[#FAFAF8] rounded-2xl p-6 sm:p-8 border border-[#E5E5E5] sm:col-span-2 md:col-span-1">
              <div className="w-12 h-12 flex items-center justify-center bg-[#8B9B8E]/10 rounded-xl mb-4">
                <i className="ri-hand-heart-line text-2xl text-[#8B9B8E]"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-[#2B2B2B] mb-2 sm:mb-3">The Boundary Blueprint</h3>
              <p className="text-[#6B6B6B] leading-relaxed text-sm sm:text-base">
                How to say "no" to inappropriate requests or "quick" curbside consults without feeling like a "bad" provider.
              </p>
            </div>

            <div className="bg-[#FAFAF8] rounded-2xl p-6 sm:p-8 border border-[#E5E5E5]">
              <div className="w-12 h-12 flex items-center justify-center bg-[#8B9B8E]/10 rounded-xl mb-4">
                <i className="ri-medicine-bottle-line text-2xl text-[#8B9B8E]"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-[#2B2B2B] mb-2 sm:mb-3">The Polypharmacy Puzzle</h3>
              <p className="text-[#6B6B6B] leading-relaxed text-sm sm:text-base">
                Deciding what to stop, what to start, and what to leave alone when a patient arrives on five different meds.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Curriculum Section */}
      <section id="curriculum" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#2B2B2B] mb-4 sm:mb-6">
              The PMHNP Foundations Mentorship
            </h2>
            <p className="text-lg sm:text-xl text-[#6B6B6B] mb-3 sm:mb-4 font-medium">
              Transition from Student to Clinician in 8 Weeks
            </p>
            <p className="text-base sm:text-lg text-[#6B6B6B] max-w-3xl mx-auto leading-relaxed">
              This is not a board review. This is a clinical deep-dive into safe decision-making, clinical confidence, and the reality of modern practice. Designed specifically for the New Graduate PMHNP.
            </p>
          </div>

          <h3 className="text-xl sm:text-2xl font-semibold text-[#2B2B2B] text-center mb-8 sm:mb-12">
            The 8-Week Curriculum Roadmap
          </h3>

          {/* Phase 1 */}
          <div className="mb-8 sm:mb-12">
            <div className="bg-[#8B9B8E] text-white px-5 sm:px-6 py-3.5 sm:py-4 rounded-t-xl">
              <h4 className="text-lg sm:text-xl font-semibold">Phase 1: Clinical Identity & Safety Foundations</h4>
            </div>
            <div className="bg-white border border-[#E5E5E5] border-t-0 rounded-b-xl overflow-hidden">
              {weeks.filter(w => w.phase === 1).map((week) => (
                <div key={week.number} className="border-b border-[#E5E5E5] last:border-b-0">
                  <button
                    onClick={() => toggleWeek(week.number)}
                    className="w-full px-5 sm:px-6 py-4 sm:py-5 flex items-center justify-between hover:bg-[#FAFAF8] transition-colors cursor-pointer text-left active:bg-[#F5F5F3]"
                  >
                    <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                      <span className="text-xs sm:text-sm font-medium text-[#8B9B8E] bg-[#8B9B8E]/10 px-2.5 sm:px-3 py-1 rounded-full whitespace-nowrap flex-shrink-0">
                        Week {week.number}
                      </span>
                      <span className="text-[#2B2B2B] font-medium text-sm sm:text-base truncate">{week.title}</span>
                    </div>
                    <i className={`ri-arrow-down-s-line text-xl text-[#6B6B6B] transition-transform flex-shrink-0 ml-2 ${openWeek === week.number ? 'rotate-180' : ''}`}></i>
                  </button>
                  {openWeek === week.number && (
                    <div className="px-5 sm:px-6 pb-4 sm:pb-5 pt-2">
                      <p className="text-[#6B6B6B] leading-relaxed sm:pl-20 text-sm sm:text-base">{week.description}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Phase 2 */}
          <div>
            <div className="bg-[#8B9B8E] text-white px-5 sm:px-6 py-3.5 sm:py-4 rounded-t-xl">
              <h4 className="text-lg sm:text-xl font-semibold">Phase 2: Clinical Execution & Integration</h4>
            </div>
            <div className="bg-white border border-[#E5E5E5] border-t-0 rounded-b-xl overflow-hidden">
              {weeks.filter(w => w.phase === 2).map((week) => (
                <div key={week.number} className="border-b border-[#E5E5E5] last:border-b-0">
                  <button
                    onClick={() => toggleWeek(week.number)}
                    className="w-full px-5 sm:px-6 py-4 sm:py-5 flex items-center justify-between hover:bg-[#FAFAF8] transition-colors cursor-pointer text-left active:bg-[#F5F5F3]"
                  >
                    <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                      <span className="text-xs sm:text-sm font-medium text-[#8B9B8E] bg-[#8B9B8E]/10 px-2.5 sm:px-3 py-1 rounded-full whitespace-nowrap flex-shrink-0">
                        Week {week.number}
                      </span>
                      <span className="text-[#2B2B2B] font-medium text-sm sm:text-base truncate">{week.title}</span>
                    </div>
                    <i className={`ri-arrow-down-s-line text-xl text-[#6B6B6B] transition-transform flex-shrink-0 ml-2 ${openWeek === week.number ? 'rotate-180' : ''}`}></i>
                  </button>
                  {openWeek === week.number && (
                    <div className="px-5 sm:px-6 pb-4 sm:pb-5 pt-2">
                      <p className="text-[#6B6B6B] leading-relaxed sm:pl-20 text-sm sm:text-base">{week.description}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Founding Member Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-12 bg-[#8B9B8E]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white mb-4 sm:mb-6">
            Become a Founding Member
          </h2>
          <p className="text-lg sm:text-xl text-white/90 leading-relaxed mb-8 sm:mb-10">
            This is our Inaugural Cohort. Secure your spot to help shape the community and receive exclusive founding member benefits.
          </p>
          <a 
            href="#waitlist" 
            className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 bg-white text-[#2B2B2B] rounded-full text-sm sm:text-base font-medium hover:bg-[#FAFAF8] transition-all cursor-pointer whitespace-nowrap active:scale-95"
          >
            Join the Founding Cohort
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </section>

      {/* The Founding Member Opportunity */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-12 bg-[#F5F5F3]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center gap-2 bg-[#8B9B8E]/10 border border-[#8B9B8E]/20 px-3 sm:px-4 py-2 rounded-full mb-4 sm:mb-6">
              <i className="ri-star-fill text-[#8B9B8E] text-xs sm:text-sm"></i>
              <span className="text-xs sm:text-sm font-medium text-[#8B9B8E]">2026 Inaugural Cohort</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#2B2B2B] mb-4 sm:mb-6">
              The Founding Member Opportunity
            </h2>
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-[#2B2B2B] mb-4 sm:mb-6">
              Join the Inaugural Cohort
            </h3>
            <p className="text-base sm:text-lg text-[#6B6B6B] max-w-3xl mx-auto leading-relaxed">
              This is the first time this 8-week mentorship is being offered. Because this is our founding group, the experience will be uniquely intimate, high-touch, and tailored specifically to the challenges you are facing in the clinic right now.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 sm:p-8 lg:p-12 mb-8 sm:mb-12 border border-[#E5E5E5]">
            <p className="text-lg sm:text-xl text-[#2B2B2B] font-medium text-center leading-relaxed">
              As a Founding Member, you aren't just a student—you are a pioneer of this community.
            </p>
          </div>

          <div className="mb-8 sm:mb-12">
            <h3 className="text-2xl sm:text-3xl font-serif text-[#2B2B2B] mb-6 sm:mb-8 text-center">
              Why Join the First Cohort?
            </h3>
            
            <div className="space-y-5 sm:space-y-6">
              <div className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E5E5E5]">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-[#8B9B8E]/10 rounded-xl flex-shrink-0">
                    <i className="ri-discuss-line text-xl sm:text-2xl text-[#8B9B8E]"></i>
                  </div>
                  <div>
                    <h4 className="text-lg sm:text-xl font-semibold text-[#2B2B2B] mb-2 sm:mb-3">Direct Influence on Case Selection</h4>
                    <p className="text-[#6B6B6B] leading-relaxed text-sm sm:text-base">
                      Founding members help shape our weekly discussions. If you are struggling with a specific clinical scenario on Tuesday, we can often integrate that logic into our session by Thursday.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E5E5E5]">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-[#8B9B8E]/10 rounded-xl flex-shrink-0">
                    <i className="ri-group-line text-xl sm:text-2xl text-[#8B9B8E]"></i>
                  </div>
                  <div>
                    <h4 className="text-lg sm:text-xl font-semibold text-[#2B2B2B] mb-2 sm:mb-3">Small-Group Intimacy</h4>
                    <p className="text-[#6B6B6B] leading-relaxed text-sm sm:text-base">
                      We are intentionally keeping this first group small to ensure every PMHNP has the space to ask "the dumb questions" and get direct, nuanced feedback on their clinical reasoning.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E5E5E5]">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-[#8B9B8E]/10 rounded-xl flex-shrink-0">
                    <i className="ri-vip-crown-line text-xl sm:text-2xl text-[#8B9B8E]"></i>
                  </div>
                  <div>
                    <h4 className="text-lg sm:text-xl font-semibold text-[#2B2B2B] mb-2 sm:mb-3">Founding Member Status</h4>
                    <p className="text-[#6B6B6B] leading-relaxed text-sm sm:text-base">
                      You will help establish the culture of this mentorship. Those who join this inaugural group will have first-priority access to future advanced modules and community events.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E5E5E5]">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-[#8B9B8E]/10 rounded-xl flex-shrink-0">
                    <i className="ri-feedback-line text-xl sm:text-2xl text-[#8B9B8E]"></i>
                  </div>
                  <div>
                    <h4 className="text-lg sm:text-xl font-semibold text-[#2B2B2B] mb-2 sm:mb-3">Direct Access</h4>
                    <p className="text-[#6B6B6B] leading-relaxed text-sm sm:text-base">
                      In a founding cohort, the feedback loop between mentor and clinician is at its shortest. Your success directly informs the future of this program.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#8B9B8E]/10 border-l-4 border-[#8B9B8E] rounded-r-xl p-6 sm:p-8">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-xl sm:text-2xl text-[#8B9B8E] flex-shrink-0"></i>
              <div>
                <p className="text-[#2B2B2B] font-semibold mb-2 text-sm sm:text-base">Note:</p>
                <p className="text-[#2B2B2B] leading-relaxed text-sm sm:text-base">
                  Because we prioritize safety and high-quality discussion, seats for this inaugural cohort are strictly limited. This ensures we can maintain the "inner circle" feel necessary for deep clinical growth.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Investment in Your Clinical Future */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-12 bg-[#F0F2F0]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#2B2B2B] mb-3 sm:mb-4">
              Investment in Your Clinical Future
            </h2>
            <p className="text-xl sm:text-2xl text-[#2B2B2B] font-semibold mb-2">
              Founding Member Access
            </p>
            <p className="text-lg sm:text-xl text-[#6B6B6B] leading-relaxed">
              Secure your spot in the Inaugural Cohort.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 sm:p-8 lg:p-12 border-2 border-[#8B9B8E]/30 shadow-lg">
            <div className="flex items-center justify-center gap-2 mb-6 sm:mb-8">
              <div className="inline-flex items-center gap-2 bg-[#8B9B8E] text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded-full">
                <i className="ri-vip-crown-fill text-base sm:text-lg"></i>
                <span className="font-semibold text-sm sm:text-base">Founding Member</span>
              </div>
            </div>

            <p className="text-base sm:text-lg text-[#2B2B2B] leading-relaxed mb-8 sm:mb-10 text-center max-w-2xl mx-auto">
              To ensure this mentorship provides the high-touch, clinical support new graduates need, we are keeping our first cohort intentionally small. As a Founding Member, you aren't just a student—you are a pioneer of this community with direct influence on our clinical case discussions.
            </p>

            <div className="space-y-5 sm:space-y-6 mb-8 sm:mb-10">
              <div className="flex items-start gap-3 sm:gap-4 bg-[#FAFAF8] rounded-xl p-5 sm:p-6">
                <div className="w-10 h-10 flex items-center justify-center bg-[#8B9B8E]/10 rounded-lg flex-shrink-0">
                  <i className="ri-price-tag-3-line text-lg sm:text-xl text-[#8B9B8E]"></i>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-[#2B2B2B] mb-2">Waitlist Exclusive Pricing</h3>
                  <p className="text-[#6B6B6B] leading-relaxed text-sm sm:text-base">
                    We are offering a one-time Founding Member Rate for this inaugural group.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 sm:gap-4 bg-[#FAFAF8] rounded-xl p-5 sm:p-6">
                <div className="w-10 h-10 flex items-center justify-center bg-[#8B9B8E]/10 rounded-lg flex-shrink-0">
                  <i className="ri-bank-card-line text-lg sm:text-xl text-[#8B9B8E]"></i>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-[#2B2B2B] mb-2">Payment Plans Available</h3>
                  <p className="text-[#6B6B6B] leading-relaxed text-sm sm:text-base">
                    We offer flexible 3-month payment options to support you as you transition into your first clinical role.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 sm:gap-4 bg-[#FAFAF8] rounded-xl p-5 sm:p-6">
                <div className="w-10 h-10 flex items-center justify-center bg-[#8B9B8E]/10 rounded-lg flex-shrink-0">
                  <i className="ri-team-line text-lg sm:text-xl text-[#8B9B8E]"></i>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-[#2B2B2B] mb-2">Limited Seating</h3>
                  <p className="text-[#6B6B6B] leading-relaxed text-sm sm:text-base">
                    Enrollment is strictly limited to 12 clinicians to maintain an intimate, deep-dive environment.
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center">
              <a 
                href="#waitlist" 
                className="inline-flex items-center justify-center gap-2 sm:gap-3 px-8 sm:px-10 py-4 sm:py-5 bg-[#8B9B8E] text-white rounded-full text-base sm:text-lg font-semibold hover:bg-[#7a8a7d] transition-all cursor-pointer whitespace-nowrap active:scale-95 shadow-lg hover:shadow-xl"
              >
                Join the Waitlist for Founding Member Pricing
                <i className="ri-arrow-right-line text-lg sm:text-xl"></i>
              </a>
              <p className="text-xs sm:text-sm text-[#6B6B6B] mt-5 sm:mt-6 leading-relaxed px-4 max-w-xl mx-auto">
                Waitlist members receive the private enrollment link and full pricing breakdown 48 hours before the public launch.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-8 sm:gap-12 items-center">
            <div className="lg:col-span-2">
              <img 
                src="https://static.readdy.ai/image/c0f393addafe305687bbcc604ae743d2/3a6148227112197ddfc3ee0a873978b7.png"
                alt="Margaret Ngumi, FNP-C / PMHNP-BC"
                className="w-full h-full object-cover object-top rounded-2xl"
              />
            </div>
            
            <div className="lg:col-span-3">
              <p className="text-xs uppercase tracking-wider text-[#8B9B8E] mb-3 sm:mb-4 font-medium">About Your Mentor</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-[#2B2B2B] mb-2 sm:mb-3 leading-tight">
                Margaret Ngumi, FNP-C / PMHNP-BC
              </h2>
              <p className="text-sm sm:text-base text-[#6B6B6B] mb-5 sm:mb-6 italic">
                Practicing Clinician | Mentor | Founding Educator
              </p>
              
              <div className="space-y-3 sm:space-y-4 text-[#2B2B2B] leading-relaxed text-sm sm:text-base">
                <p>
                  I know exactly what it feels like to step into a clinic for the first time, carrying the weight of full clinical responsibility. The gap between what we learn in our PMHNP programs and the reality of a 20-minute medication visit is often wider than we expect.
                </p>
                <p>
                  I've spent my career navigating the complexities of inpatient, outpatient, and telehealth psychiatry. I've seen the "messy" side of practice—the polypharmacy puzzles, the high-stakes safety calls, and the boundary-pushing encounters—that textbooks simply don't prepare us for.
                </p>
              </div>

              <div className="mt-6 sm:mt-8">
                <h3 className="text-lg sm:text-xl font-semibold text-[#2B2B2B] mb-2 sm:mb-3">
                  Why I Created This Mentorship
                </h3>
                <p className="text-[#2B2B2B] leading-relaxed mb-3 sm:mb-4 text-sm sm:text-base">
                  I founded this program because new PMHNPs deserve more than just a board review. You deserve a bridge.
                </p>
                <p className="text-[#2B2B2B] leading-relaxed text-sm sm:text-base">
                  My goal is to help you move past the "imposter" phase by giving you the same frameworks I use in my own daily practice. I don't want you to just survive your first year; I want you to build a sustainable, confident clinical identity that prevents burnout and protects your patients.
                </p>
              </div>

              <div className="mt-6 sm:mt-8 bg-[#F5F5F3] rounded-xl p-5 sm:p-6 border-l-4 border-[#8B9B8E]">
                <h4 className="text-base sm:text-lg font-semibold text-[#2B2B2B] mb-2">
                  A Note on the Inaugural Cohort
                </h4>
                <p className="text-[#2B2B2B] leading-relaxed text-sm sm:text-base">
                  As we launch this first cohort, I am fully invested in your success. This isn't a recorded, "set it and forget it" course. I am in the trenches with you, facilitating every live discussion and tailoring our case studies to the real-world obstacles you are facing in your clinic right now.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Discovery Call Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#2B2B2B] mb-4 sm:mb-6">
              Not Sure if This is the Right Fit?
            </h2>
            <p className="text-xl sm:text-2xl lg:text-3xl font-semibold text-[#2B2B2B] mb-3 sm:mb-4">
              Let's Chat: 15-Minute Mentorship Discovery Call
            </p>
            <p className="text-base sm:text-lg text-[#6B6B6B] max-w-3xl mx-auto leading-relaxed">
              The transition from student to clinician is a big leap. I want to make sure this 8-week program is the bridge you actually need before you commit.
            </p>
          </div>

          <div className="bg-[#FAFAF8] rounded-2xl p-6 sm:p-8 lg:p-12 border border-[#E5E5E5] mb-8 sm:mb-10">
            <p className="text-base sm:text-lg text-[#2B2B2B] leading-relaxed mb-6 sm:mb-8">
              If you have questions about the curriculum, the schedule, or how this specifically supports your current practice setting (Inpatient, Outpatient, or Telehealth), let's get on a quick call.
            </p>

            <h3 className="text-lg sm:text-xl font-semibold text-[#2B2B2B] mb-5 sm:mb-6">
              What we will cover in 15 minutes:
            </h3>

            <div className="space-y-4 sm:space-y-5">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#8B9B8E]/10 rounded-lg flex-shrink-0">
                  <i className="ri-stethoscope-line text-lg sm:text-xl text-[#8B9B8E]"></i>
                </div>
                <div>
                  <h4 className="text-base sm:text-lg font-semibold text-[#2B2B2B] mb-1">Your Clinical Gaps</h4>
                  <p className="text-[#6B6B6B] leading-relaxed text-sm sm:text-base">
                    What is your biggest "pain point" in practice right now?
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#8B9B8E]/10 rounded-lg flex-shrink-0">
                  <i className="ri-compass-line text-lg sm:text-xl text-[#8B9B8E]"></i>
                </div>
                <div>
                  <h4 className="text-base sm:text-lg font-semibold text-[#2B2B2B] mb-1">Program Alignment</h4>
                  <p className="text-[#6B6B6B] leading-relaxed text-sm sm:text-base">
                    Will the 8-week roadmap solve the specific challenges you're facing?
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#8B9B8E]/10 rounded-lg flex-shrink-0">
                  <i className="ri-vip-crown-line text-lg sm:text-xl text-[#8B9B8E]"></i>
                </div>
                <div>
                  <h4 className="text-base sm:text-lg font-semibold text-[#2B2B2B] mb-1">Founding Member Details</h4>
                  <p className="text-[#6B6B6B] leading-relaxed text-sm sm:text-base">
                    Clarification on the live format, founding rates, and payment plans.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#8B9B8E]/10 border border-[#8B9B8E]/20 rounded-xl p-5 sm:p-6 mb-8 sm:mb-10">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-lg sm:text-xl text-[#8B9B8E] flex-shrink-0 mt-0.5"></i>
              <p className="text-[#2B2B2B] leading-relaxed text-sm sm:text-base">
                <strong className="font-semibold">Please Note:</strong> These are professional discovery calls to discuss the mentorship program. For legal and ethical reasons, clinical advice or supervision for specific patient cases cannot be provided during these calls.
              </p>
            </div>
          </div>

          <div className="text-center">
            <a
              href="https://calendly.com/havenpsychiatrynp/free-15-minute-consultation"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 bg-[#2B2B2B] text-white rounded-full text-sm sm:text-base font-medium hover:bg-[#1a1a1a] transition-all cursor-pointer whitespace-nowrap active:scale-95"
            >
              Book Your 15-Minute Clarity Call
              <i className="ri-calendar-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Waitlist CTA Section */}
      <section id="waitlist" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-12 bg-[#2B2B2B]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white mb-4 sm:mb-6">
            Join the Waitlist
          </h2>
          <p className="text-base sm:text-lg text-[#B5B5B5] mb-8 sm:mb-12 leading-relaxed">
            I am preparing to launch the first cohort of this mentorship. Join the waitlist for founding-member updates and early access to enrollment.
          </p>

          {!formSubmitted ? (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 sm:gap-4 max-w-lg mx-auto" data-readdy-form>
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => setFormData({ email: e.target.value })}
                required
                className="flex-1 px-5 sm:px-6 py-3.5 sm:py-4 bg-white/10 border border-white/20 rounded-full text-white placeholder-[#B5B5B5] focus:outline-none focus:border-[#8B9B8E] transition-colors text-sm sm:text-base"
              />
              <button
                type="submit"
                className="px-6 sm:px-8 py-3.5 sm:py-4 bg-[#8B9B8E] text-white rounded-full font-semibold hover:bg-[#7a8a7d] transition-all inline-flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer active:scale-95 text-sm sm:text-base shadow-lg hover:shadow-xl"
              >
                Join Waitlist
                <i className="ri-arrow-right-line text-lg sm:text-xl"></i>
              </button>
            </form>
          ) : (
            <div className="bg-[#8B9B8E]/20 border border-[#8B9B8E]/30 rounded-2xl p-6 sm:p-8 max-w-lg mx-auto">
              <i className="ri-checkbox-circle-fill text-[#8B9B8E] text-3xl sm:text-4xl mb-3 sm:mb-4"></i>
              <p className="text-white text-base sm:text-lg">Thank you for joining the waitlist. You'll receive updates soon.</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 sm:py-8 px-4 sm:px-6 lg:px-12 bg-[#1a1a1a] text-center">
        <p className="text-[#B5B5B5] text-xs sm:text-sm">
          © 2025 Margaret Ngumi
        </p>
        <a 
          href="https://readdy.ai/?ref=logo" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-[#8B9B8E] text-xs sm:text-sm hover:text-[#7a8a7d] transition-colors mt-2 inline-block cursor-pointer"
        >
          Powered by Readdy
        </a>
      </footer>
    </div>
  );
}
